import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import { useLocation } from 'Pods/Shared/ContextBasedRouter'
import useApiPost from 'Pods/Shared/Hooks/useApiPost'
import { TURN_STATUS, MAX_ATTACHMENTS, RUN_TERMINAL_STATUSES } from 'Pods/ThreeDStudio/constants'
import useApplyImageToThreeD from 'Pods/ThreeDStudio/Hooks/useApplyImageToThreeD'
import useImageAssetUpload from 'Pods/ThreeDStudio/Hooks/useImageAssetUpload'
import useRequireGenerationAccess from 'Pods/ThreeDStudio/Hooks/useRequireGenerationAccess'
import useSessionImageGenerations from 'Pods/ThreeDStudio/Hooks/useSessionImageGenerations'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import { buildImageGeneratePayload } from 'Pods/ThreeDStudio/Services/buildImageGeneratePayload'
import { replaceWithImageEditUrl } from 'Pods/ThreeDStudio/Shared/Utils/imageEditUrl'
import { imageSchemaFor } from 'Pods/ThreeDStudio/Shared/Utils/imageModels'
import {
  getCurrentSessionId,
  getImageGeneratorSessionId,
  isEditScreenRun,
  selectImageGeneratorSessionId,
  selectSessionId,
  subscribeToGeneratorRun,
} from 'Pods/ThreeDStudio/Shared/Utils/imageRun'
import preloadImage from 'Pods/ThreeDStudio/Shared/Utils/preloadImage'
import { threeDStudioStore, useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { setInputImage } from 'Pods/ThreeDStudio/Store/actions/modelSettings'
import {
  closeImageAssistant,
  openFullImageEditor,
  openGeneratedImageInEditor,
  openImageEditor,
  setImageAssistantSessionId,
  setImageGeneratorSessionId,
} from 'Pods/ThreeDStudio/Store/actions/ui'
import { IMAGE_ACTION } from 'Pods/ThreeDStudio/Store/constants'

import { EMPTY_ARRAY, EMPTY_OBJECT, NOOP } from 'Utils/stableDefaults'

import { CREATE_TAGS } from '../../mockData'

// A restored run only keeps spinning while it is genuinely in flight; one that
// settled without an image (failed, canceled, timed out, rejected) shows the retry
// state instead of a spinner that can never resolve.
const turnStatusForRun = (status) => {
  if (status === 'succeeded') {
    return TURN_STATUS.DONE
  }

  return RUN_TERMINAL_STATUSES.has(status) ? TURN_STATUS.FAILED : TURN_STATUS.LOADING
}

// The AI Assistant panel and the Image Generator page each keep their own conversation,
// so each reads and records the session its runs live in through its own store field.
const ASSISTANT_SESSION = {
  select: selectSessionId,
  get: getCurrentSessionId,
  set: setImageAssistantSessionId,
}
const IMAGE_GENERATOR_SESSION = {
  select: selectImageGeneratorSessionId,
  get: getImageGeneratorSessionId,
  set: setImageGeneratorSessionId,
}

// Each image action caps input_image differently, so the reference-image count decides
// which action the prompt is sent as: none is text-to-image, a single reference is
// similar-to (max 1), and several are blended by multi-image-edit (2-8). The backend
// then ranks the providers that offer that action.
const imageActionForCount = (imageCount) => {
  if (imageCount === 0) {
    return IMAGE_ACTION.TEXT_TO_IMAGE
  }

  return imageCount === 1 ? IMAGE_ACTION.SIMILAR_TO : IMAGE_ACTION.MULTI_IMAGE_EDIT
}

const currentIsPrivate = () => threeDStudioStore.getState().ui.isImageAssistantPrivate

// The top-level run data the composer settings add: the picked model locks the provider
// (the schema's catalog id for this action) and the Private flag rides along; the aspect
// ratio and variations reach canonical_params through the builder's store mapping. Null
// settings - the composer shows no controls - leave the provider to the backend's ranking,
// as does a picked model that lacks the action (imageSchemaFor then falls back to another
// provider's schema).
const settingsPayload = (turnSettings, schema) => {
  if (!turnSettings) {
    return EMPTY_OBJECT
  }

  const isProviderLocked = schema.provider === turnSettings.modelId
  return { ...(isProviderLocked ? { model_id: schema.model_id } : {}), private: turnSettings.isPrivate }
}

const toResult = alt => asset => ({ id: asset.assetId, url: asset.fileUrl, alt })

// /ai-image/:slug opens a run's first image, so only the image of a single-image run can be
// shared by its run's slug.
const toResults = (assets, alt, slug) => {
  const results = assets.map(toResult(alt))
  return results.length === 1 && slug ? [{ ...results[0], slug }] : results
}

// Opens a Create page result in the full-width editor. The address bar also moves to the
// image's own URL when the result is the only image of its run: /ai-image/:slug opens a run's
// first image, so a variation's URL would reopen a sibling on reload.
const openResultInEditor = ({ image, sessionId, runId, slug, isOnlyImage }) => {
  const returnUrl = isOnlyImage ? replaceWithImageEditUrl(slug) : null
  openFullImageEditor({
    image, sessionId, runId, canReturnToCreate: true, returnUrl,
  })
}

/**
 * Owns the Create tab's state: the composer (prompt + reference attachments) and
 * the conversation of generation turns, plus the handlers wiring those to the
 * generation backend and the store.
 * @param {{ isFullWidth?: boolean, hasGenerationSettings?: boolean }} options - isFullWidth
 *   is the Image Generator page, where there is no assistant panel: generated and edited
 *   images open the full-width editor instead of the panel's Edit tab. hasGenerationSettings
 *   is whether the composer shows the model, aspect ratio, variations and Private controls;
 *   their values are read from the store's imageSettings slice at send time.
 */
export default function useCreateTab({ isFullWidth = false, hasGenerationSettings = false } = EMPTY_OBJECT) {
  const t = useTranslation()
  const resultAlt = t('imageAssistantPanel.createTab.result.lightbox')
  const [prompt, setPrompt] = useState('')
  const [attachments, setAttachments] = useState(EMPTY_ARRAY)
  const [messages, setMessages] = useState(EMPTY_ARRAY)

  // An image's Regenerate, for someone else's image, lands here with that image's prompt in the
  // navigation state. Applied while rendering, once per navigation (keyed on location.key),
  // because the page may already be mounted under the image's modal and would not re-run a
  // useState initializer. Only the Image Generator page takes it; the assistant panel's own
  // conversation is left alone.
  const location = useLocation()
  const prefillPrompt = isFullWidth ? location?.state?.prefillPrompt : undefined
  const [prefilledKey, setPrefilledKey] = useState(null)
  if (prefillPrompt && location.key !== prefilledKey) {
    setPrefilledKey(location.key)
    setPrompt(prefillPrompt)
  }

  const session = isFullWidth ? IMAGE_GENERATOR_SESSION : ASSISTANT_SESSION
  const sessionId = useThreeDStudioStore(session.select)
  const { generations: sessionGenerations } = useSessionImageGenerations(sessionId)
  // The session holds every image run, including the Edit screen's masked and
  // instruction edits; only the ones started from this composer belong in the
  // conversation.
  const generations = useMemo(
    () => sessionGenerations.filter(run => !isEditScreenRun(run)),
    [sessionGenerations],
  )

  const fileInputRef = useRef(null)
  const idRef = useRef(0)
  const isMountedRef = useRef(true)
  const subscriptionsRef = useRef(new Map())
  const hydratedRef = useRef(false)
  const resetKeyRef = useRef(threeDStudioStore.getState().ui.imageGeneratorResetKey)

  useEffect(() => () => {
    isMountedRef.current = false
    subscriptionsRef.current.forEach(subscription => subscription.unsubscribe())
    subscriptionsRef.current.clear()
  }, [])

  const queryClient = useQueryClient()
  const { postData } = useApiPost()
  const uploadImageAsset = useImageAssetUpload()
  const { requireGenerationAccess, isAccessPending } = useRequireGenerationAccess()

  // A run sent before the Image Generator was reset still answers after it. Recording its
  // session then would hand the abandoned conversation to the new starting screen.
  const recordSession = useCallback((generatorSessionId) => {
    const isStale = isFullWidth
      && threeDStudioStore.getState().ui.imageGeneratorResetKey !== resetKeyRef.current
    if (generatorSessionId && !isStale) {
      session.set(generatorSessionId)
    }
  }, [isFullWidth, session])

  const nextId = () => {
    idRef.current += 1
    return idRef.current
  }

  // Reference-image attachments drive which quick-action tags are offered.
  const tags = useMemo(() => {
    if (attachments.length === 0) {
      return EMPTY_ARRAY
    }
    return attachments.length === 1 ? CREATE_TAGS.SINGLE : CREATE_TAGS.MULTI
  }, [attachments])

  const isGenerating = useMemo(
    () => messages.some(message => message.status === TURN_STATUS.LOADING),
    [messages],
  )

  const setTurn = useCallback((turnId, updates) => {
    if (!isMountedRef.current) {
      return
    }
    setMessages(prev => prev.map(message => (
      message.id === turnId ? { ...message, ...updates } : message
    )))
  }, [])

  const failTurn = useCallback((turnId) => {
    setTurn(turnId, { status: TURN_STATUS.FAILED, results: EMPTY_ARRAY })
  }, [setTurn])

  // Subscribe to the run's websocket channel and resolve the turn when the backend
  // broadcasts the terminal update with the generated image (or marks it failed).
  // Keyed by run id: restoring the conversation re-subscribes to in-flight runs, and a
  // run the live send is already watching must not get a second subscription.
  // A run asked for several variations leaves the user on the grid to pick one, so only
  // single-image runs jump into the editor. A restored run's request is unknown, so the
  // returned count decides for it.
  const subscribeToRun = useCallback((runId, turnId, requestedVariations = 1) => {
    const runKey = String(runId)
    if (subscriptionsRef.current.has(runKey)) {
      return
    }

    const subscription = subscribeToGeneratorRun(runId, {
      onImage: async (asset, assets, attributes) => {
        subscriptionsRef.current.delete(runKey)
        queryClient.refetchQueries({ queryKey: ['libraryItems', 'images'], exact: false })
        const results = toResults(assets, resultAlt, attributes?.slug)
        const [result] = results
        // The loading cards stay up until every image can paint: an image mounted before it
        // loads has no height, so the turn showed only its collapsed border.
        await Promise.all(results.map(({ url }) => preloadImage(url)))
        if (!isMountedRef.current) {
          return
        }
        // Only the Image Generator page writes an image's URL, so only its turns keep the slug.
        const slug = isFullWidth ? attributes?.slug : undefined
        setTurn(turnId, { status: TURN_STATUS.DONE, results, ...(slug && { slug }) })
        if (Math.max(requestedVariations, results.length) > 1) {
          return
        }
        if (isFullWidth) {
          openResultInEditor({
            image: result, sessionId: session.get(), runId, slug, isOnlyImage: true,
          })
          return
        }
        // Auto-open bypasses the handleApply click that normally sets the 3D input.
        if (openGeneratedImageInEditor(result)) {
          setInputImage(result.id, result.url)
        }
      },
      onFail: () => {
        subscriptionsRef.current.delete(runKey)
        failTurn(turnId)
      },
    })

    if (subscription) {
      subscriptionsRef.current.set(runKey, subscription)
    } else {
      failTurn(turnId)
    }
  }, [failTurn, setTurn, isFullWidth, queryClient, session, resultAlt])

  // Restore the conversation from the session's saved image generations once, after a
  // reload: succeeded runs become finished turns; still-running ones become loading turns
  // that reconnect to their websocket. We never clobber turns already in progress.
  useEffect(() => {
    if (hydratedRef.current || generations.length === 0) {
      return
    }
    hydratedRef.current = true

    const restored = generations.map((run) => {
      const outputs = (run.outputAssets || []).filter(asset => asset?.fileUrl)
      const isDone = run.status === 'succeeded'
      return {
        id: run.id,
        runId: run.id,
        prompt: run.prompt,
        images: (run.inputAssets || []).map(asset => ({ id: asset.assetId, url: asset.fileUrl })),
        inputAssetIds: (run.inputAssets || []).map(asset => asset.assetId),
        status: turnStatusForRun(run.status),
        results: isDone ? toResults(outputs, resultAlt, run.slug) : EMPTY_ARRAY,
        ...(isFullWidth && run.slug && { slug: run.slug }),
      }
    })
    idRef.current = restored.reduce((max, turn) => Math.max(max, Number(turn.id) || 0), idRef.current)
    setMessages(prev => (prev.length > 0 ? prev : restored))

    generations.forEach((run) => {
      if (!RUN_TERMINAL_STATUSES.has(run.status)) {
        subscribeToRun(run.id, run.id)
      }
    })
  }, [generations, subscribeToRun, resultAlt, isFullWidth])

  // Create an image run, attaching it to the existing session when one exists, then wait
  // for the websocket terminal update to surface the image.
  //
  // The action rides on use_case, which is what CreateFromProvider#infer_action reads for
  // an image_generation carrying input_image. A model_id from the composer's settings
  // locks the run to that provider, so it cannot fail over to the other one; without it
  // the backend ranks flux against nano-banana itself.
  const runGeneration = useCallback((turnId, turnPrompt, inputAssetIds, turnSettings) => {
    const activeSessionId = session.get()
    const imageCount = Array.isArray(inputAssetIds) ? inputAssetIds.length : 0
    const schema = imageSchemaFor(imageActionForCount(imageCount), turnSettings?.modelId)
    const values = {
      prompt: turnPrompt,
      input_image: imageCount > 0 ? inputAssetIds.map(assetId => ({ asset_id: assetId })) : undefined,
    }

    postData({
      path: '/api/internal/generator/runs',
      data: buildImageGeneratePayload(schema, values, turnSettings, {
        ...settingsPayload(turnSettings, schema),
        ...(activeSessionId ? { generator_session_id: activeSessionId } : {}),
      }),
      onSuccess: (response) => {
        const { id, generatorSessionId } = response.data.data.attributes
        recordSession(generatorSessionId)
        setTurn(turnId, { runId: id })
        queryClient.refetchQueries({ queryKey: ['libraryItems', 'images'], exact: false })
        subscribeToRun(id, turnId, turnSettings?.variations)
      },
      onError: () => failTurn(turnId),
    })
  }, [postData, subscribeToRun, failTurn, setTurn, session, recordSession, queryClient])

  // Upload any reference images to reusable Generator::Assets, then start the run.
  // Already-uploaded ids (regenerate/retry) are reused so we never re-upload.
  const startGeneration = useCallback((turnId, turnPrompt, turnImages, existingAssetIds, turnSettings) => {
    if (existingAssetIds && existingAssetIds.length > 0) {
      runGeneration(turnId, turnPrompt, existingAssetIds, turnSettings)
      return
    }
    if (!turnImages || turnImages.length === 0) {
      runGeneration(turnId, turnPrompt, EMPTY_ARRAY, turnSettings)
      return
    }

    const activeSessionId = session.get()
    Promise.all(turnImages.map(image => uploadImageAsset(image.file, { sessionId: activeSessionId })))
      .then((assetIds) => {
        if (!isMountedRef.current) {
          return
        }
        setTurn(turnId, { inputAssetIds: assetIds })
        runGeneration(turnId, turnPrompt, assetIds, turnSettings)
      })
      .catch(() => failTurn(turnId))
  }, [uploadImageAsset, runGeneration, setTurn, failTurn, session])

  // The settings in effect right now, captured when a turn is sent so later changes in
  // the popover only apply to the next image. Null while the composer shows no controls.
  const snapshotSettings = useCallback(
    () => (hasGenerationSettings
      ? { ...threeDStudioStore.getState().imageSettings, isPrivate: currentIsPrivate() }
      : null),
    [hasGenerationSettings],
  )

  const handlePromptChange = useCallback(event => setPrompt(event.target.value), [])

  const handleTagClick = useCallback(tag => setPrompt(tag), [])

  const handleUploadClick = useCallback(() => fileInputRef.current?.click(), [])

  const handleRemoveAttachment = useCallback((id) => {
    setAttachments((prev) => {
      const removed = prev.find(image => image.id === id)
      if (removed) {
        URL.revokeObjectURL(removed.url)
      }
      return prev.filter(image => image.id !== id)
    })
  }, [])

  const handleFilesChange = useCallback((event) => {
    const picked = Array.from(event.target.files || [])
    const remaining = MAX_ATTACHMENTS - attachments.length
    if (picked.length && remaining > 0) {
      const added = picked
        .slice(0, remaining)
        .map(file => ({ id: nextId(), url: URL.createObjectURL(file), file }))
      setAttachments(prev => [...prev, ...added])
    }
    // Reset so picking the same file again still fires change.
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [attachments])

  const handleSend = useCallback(() => {
    const trimmed = prompt.trim()
    if (!trimmed || !requireGenerationAccess()) {
      return
    }

    const turnId = nextId()
    const turnImages = attachments
    const turnSettings = snapshotSettings()
    setMessages(prev => [
      ...prev,
      {
        id: turnId,
        prompt: trimmed,
        images: turnImages,
        settings: turnSettings,
        expectedImages: turnSettings?.variations ?? 1,
        status: TURN_STATUS.LOADING,
        results: EMPTY_ARRAY,
      },
    ])
    setPrompt('')
    setAttachments(EMPTY_ARRAY)
    startGeneration(turnId, trimmed, turnImages, null, turnSettings)
  }, [prompt, attachments, startGeneration, requireGenerationAccess, snapshotSettings])

  // Regenerate re-sends the same request and appends only the new image — the
  // sent prompt/images are not shown again (continuation turn).
  const handleRegenerate = useCallback((turn) => {
    if (!turn.runId || !requireGenerationAccess()) {
      return
    }
    const turnId = nextId()
    // The backend copies the source run's num_images, so a restored turn with no
    // settings falls back to how many images the source returned.
    const expectedImages = turn.settings?.variations ?? turn.results.length
    setMessages(prev => [
      ...prev,
      {
        id: turnId,
        prompt: turn.prompt,
        images: turn.images,
        settings: turn.settings,
        expectedImages,
        status: TURN_STATUS.LOADING,
        results: EMPTY_ARRAY,
        continuation: true,
      },
    ])

    postData({
      path: `/api/internal/generator/image-generations/${turn.runId}/regenerate`,
      data: {},
      onSuccess: (response) => {
        const { id, generatorSessionId } = response.data.data.attributes
        recordSession(generatorSessionId)
        setTurn(turnId, { runId: id })
        queryClient.refetchQueries({ queryKey: ['libraryItems', 'images'], exact: false })
        subscribeToRun(id, turnId, expectedImages)
      },
      onError: () => failTurn(turnId),
    })
  }, [postData, setTurn, failTurn, subscribeToRun, requireGenerationAccess, recordSession, queryClient])

  // Retry a failed turn in place: flip it back to loading and re-run.
  const handleGenerateAgain = useCallback((turn) => {
    if (!requireGenerationAccess()) {
      return
    }
    // A retry keeps the settings the turn was sent with, so flipping Private afterwards
    // cannot publish an image the user asked to keep private. Restored turns carry none
    // and fall back to the current settings.
    const turnSettings = turn.settings ?? snapshotSettings()
    setTurn(turn.id, {
      status: TURN_STATUS.LOADING,
      results: EMPTY_ARRAY,
      expectedImages: turnSettings?.variations ?? 1,
    })
    startGeneration(turn.id, turn.prompt, turn.images, turn.inputAssetIds, turnSettings)
  }, [startGeneration, setTurn, requireGenerationAccess, snapshotSettings])

  const applyImageToThreeD = useApplyImageToThreeD(NOOP)

  // The image is already a Generator::Asset (ai_generated output), so reuse its id as
  // the 3D input instead of re-uploading the file. The Image Generator page has no
  // generate form behind it, so there the user is taken to /ai.
  const handleApply = useCallback((image) => {
    if (isFullWidth) {
      applyImageToThreeD(image)
      return
    }

    setInputImage(image.id, image.url)
    closeImageAssistant()
  }, [isFullWidth, applyImageToThreeD])

  const handleEdit = useCallback((image) => {
    if (!isFullWidth) {
      openImageEditor(image)
      return
    }

    const turn = messages.find(message => message.results.some(result => result.id === image.id))
    openResultInEditor({
      image,
      sessionId: session.get(),
      runId: turn?.runId,
      slug: turn?.slug,
      isOnlyImage: turn?.results.length === 1,
    })
  }, [isFullWidth, messages, session])

  return {
    prompt,
    attachments,
    messages,
    tags,
    isGenerating,
    isAccessPending,
    isUploadDisabled: attachments.length >= MAX_ATTACHMENTS,
    fileInputRef,
    handlePromptChange,
    handleTagClick,
    handleUploadClick,
    handleRemoveAttachment,
    handleFilesChange,
    handleSend,
    handleRegenerate,
    handleGenerateAgain,
    handleApply,
    handleEdit,
  }
}
